import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  InputAdornment,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowForwardRounded,
  ContentCopyRounded,
  InboxOutlined,
  LockOutlined,
  SearchRounded,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { OwnerFilter, RegistrationStatus } from "../../../Models/ambassador";
import {
  useDealOwners,
  useDealSummary,
  useDeals,
} from "../../../Query/useAmbassador";
import { useDebouncedCallback } from "../../../Hooks/useDebouncedCallback";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  RegistrationStatusChip,
  StatGrid,
  StatTile,
} from "../Common/AmbassadorUi";
import { ambassadorPaths } from "../Common/ambassadorNavigation";
import { displayName, formatDate } from "../Common/format";
import {
  REGISTRATION_STATUSES,
  useAmbassadorLabels,
} from "../Common/useAmbassadorLabels";

const PAGE_SIZE = 15;

// The queue only holds deals waiting for como.
const OPEN_STATUSES = [
  RegistrationStatus.SUBMITTED,
  RegistrationStatus.UNDER_REVIEW,
  RegistrationStatus.QUESTIONS_PENDING,
];

export const InternalNotice: React.FC = () => {
  const { t } = useTranslation("ambassador");
  return (
    <Alert
      icon={<LockOutlined fontSize="small" />}
      severity="info"
      sx={{ mb: 3 }}
    >
      {t("inbox.internalNotice")}
    </Alert>
  );
};

/** como's list of submitted deals: the open queue, or every deal. */
const DealInbox: React.FC<{ mode: "inbox" | "all" }> = ({ mode }) => {
  const { t, i18n } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const navigate = useNavigate();
  const { primary, accentText } = useDashboardColors();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<RegistrationStatus | null>(null);
  const [owner, setOwner] = useState<OwnerFilter>(null);

  const { data: summary } = useDealSummary();
  const { data: owners = [] } = useDealOwners();
  const deals = useDeals({
    page,
    page_size: PAGE_SIZE,
    search: search || undefined,
    registration_status: status,
    open_only: mode === "inbox" || undefined,
    owner: owner ?? undefined,
  });

  const updateSearch = useDebouncedCallback((value: string) => {
    setSearch(value.trim());
    setPage(1);
  }, 300);

  const statuses = (
    mode === "inbox" ? OPEN_STATUSES : REGISTRATION_STATUSES
  ).filter((value) => value !== RegistrationStatus.DRAFT);
  const pageCount = Math.ceil((deals.data?.total ?? 0) / PAGE_SIZE);

  return (
    <>
      <PageHeader
        title={t(mode === "inbox" ? "inbox.title" : "inbox.allTitle")}
        subtitle={t(mode === "inbox" ? "inbox.subtitle" : "inbox.allSubtitle")}
      />
      <InternalNotice />

      <StatGrid>
        <StatTile
          label={t("inbox.stats.submitted")}
          value={summary?.submitted}
          highlight={!!summary?.submitted}
        />
        <StatTile
          label={t("inbox.stats.underReview")}
          value={summary?.under_review}
        />
        <StatTile
          label={t("inbox.stats.questions")}
          value={summary?.questions_pending}
        />
        {mode === "inbox" ? (
          <StatTile
            label={t("inbox.stats.assignedToMe")}
            value={summary?.assigned_to_me}
          />
        ) : (
          <StatTile label={t("inbox.stats.closed")} value={summary?.closed} />
        )}
      </StatGrid>

      <Panel
        title={t(mode === "inbox" ? "inbox.listTitle" : "inbox.allListTitle")}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) 1fr 1fr" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <TextField
            size="small"
            value={searchInput}
            placeholder={t("inbox.searchPlaceholder")}
            onChange={(event) => {
              setSearchInput(event.target.value);
              updateSearch(event.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: color.gray400 }} />
                </InputAdornment>
              ),
            }}
            inputProps={{ "aria-label": t("inbox.searchPlaceholder") }}
          />
          <TextField
            select
            size="small"
            label={t("inbox.statusFilter")}
            value={status ?? ""}
            onChange={(event) => {
              setStatus(
                event.target.value === "" ? null : Number(event.target.value),
              );
              setPage(1);
            }}
          >
            <MenuItem value="">{t("common.all")}</MenuItem>
            {statuses.map((value) => (
              <MenuItem key={value} value={value}>
                {labels.registrationStatus(value)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t("inbox.ownerFilter")}
            value={owner ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              setOwner(
                value === ""
                  ? null
                  : value === "me" || value === "none"
                    ? value
                    : Number(value),
              );
              setPage(1);
            }}
          >
            <MenuItem value="">{t("common.all")}</MenuItem>
            <MenuItem value="me">{t("inbox.ownerMe")}</MenuItem>
            <MenuItem value="none">{t("inbox.ownerNone")}</MenuItem>
            {owners.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {displayName(user)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {deals.isLoading ? (
          <LoadingState />
        ) : deals.isError ? (
          <ErrorState onRetry={() => deals.refetch()} />
        ) : !deals.data?.deals.length ? (
          <EmptyState
            icon={<InboxOutlined />}
            title={t(
              mode === "inbox" ? "inbox.emptyTitle" : "inbox.allEmptyTitle",
            )}
            text={t("inbox.emptyText")}
          />
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("inbox.columns.deal")}</TableCell>
                    <TableCell>{t("inbox.columns.ambassador")}</TableCell>
                    <TableCell>{t("inbox.columns.owner")}</TableCell>
                    <TableCell>{t("inbox.columns.submitted")}</TableCell>
                    <TableCell>{t("inbox.columns.status")}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deals.data.deals.map((deal) => (
                    <TableRow
                      key={deal.id}
                      hover
                      onClick={() => navigate(ambassadorPaths.review(deal.id))}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell sx={{ minWidth: 200 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 14,
                              color: primary,
                            }}
                          >
                            {deal.title}
                          </Typography>
                          {deal.has_possible_duplicates && (
                            <Tooltip title={t("inbox.possibleDuplicate")}>
                              <ContentCopyRounded
                                sx={{ fontSize: 16, color: color.orange600 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 12, color: color.gray500 }}>
                          {deal.deal_number} · {deal.company_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {displayName(deal.ambassador) || "–"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: deal.owner ? color.gray900 : color.gray400,
                        }}
                      >
                        {displayName(deal.owner) || t("inbox.unassigned")}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDate(deal.submitted_at, i18n.language)}
                      </TableCell>
                      <TableCell>
                        <RegistrationStatusChip
                          status={deal.registration_status}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <ArrowForwardRounded
                          sx={{ fontSize: 18, color: accentText }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                mt: 2,
              }}
            >
              <Typography sx={{ fontSize: 13, color: color.gray500 }}>
                {t("common.shownOf", {
                  shown: deals.data.deals.length,
                  total: deals.data.total,
                })}
              </Typography>
              {pageCount > 1 && (
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size="small"
                />
              )}
            </Box>
          </>
        )}
      </Panel>
    </>
  );
};

export default DealInbox;
